import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { getTransferCorrections, getTransferEvents } from "./transferApi";

const validationTitle = {
  pending: "Awaiting validation",
  valid: "Validated",
  warning: "Validated with warning",
  invalid: "Invalid transfer; excluded from totals",
};

const validationSeverity = {
  pending: "info",
  valid: "success",
  warning: "warning",
  invalid: "error",
};

export default function TransferRecordDetails({ transfer }) {
  const [audit, setAudit] = useState({ status: "loading", events: [], corrections: [] });

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      getTransferEvents(transfer.id, controller.signal),
      getTransferCorrections(transfer.id, controller.signal),
    ])
      .then(([events, corrections]) => setAudit({ status: "ready", events, corrections }))
      .catch((error) => {
        if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
          setAudit({ status: "unavailable", events: [], corrections: [] });
        }
      });
    return () => controller.abort();
  }, [transfer.id]);

  const messageList = Array.isArray(transfer.validationMessages) ? transfer.validationMessages : [];
  const gallons = (value) => value == null ? "--" : `${Number(value).toLocaleString()} gal`;

  return (
    <MDBox p={2}>
      <Alert severity={validationSeverity[transfer.validationStatus] ?? "info"}>
        {validationTitle[transfer.validationStatus] ?? "Transfer status unavailable"}
        {messageList.length > 0 && ` · ${messageList.join(", ")}`}
      </Alert>
      <MDBox mt={1.5} display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1}>
        <MDTypography variant="caption" color="text">Reported: {gallons(transfer.reportedGallons)}</MDTypography>
        <MDTypography variant="caption" color="text">Calculated: {gallons(transfer.calculatedGallons)}</MDTypography>
        <MDTypography variant="caption" color="text">Variance: {gallons(transfer.varianceGallons)}</MDTypography>
        <MDTypography variant="caption" color="text">Start / stop: {gallons(transfer.startGallons)} / {gallons(transfer.stopGallons)}</MDTypography>
        <MDTypography variant="caption" color="text">Operator: {transfer.operator?.name || "--"}</MDTypography>
        <MDTypography variant="caption" color="text">Asset: {transfer.assetId || "--"}</MDTypography>
        <MDTypography variant="caption" color="text">Route / job: {transfer.routeId ?? "--"} / {transfer.jobId ?? "--"}</MDTypography>
        <MDTypography variant="caption" color="text">Events: {transfer.eventCount} · Restatements: {transfer.restatementCount}</MDTypography>
        <MDTypography variant="caption" color="text">Start location: {formatLocation(transfer.startLocation)}</MDTypography>
        <MDTypography variant="caption" color="text">Stop location: {formatLocation(transfer.stopLocation)}</MDTypography>
      </MDBox>
      {transfer.corrected && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          This record was corrected. Version {transfer.version}.
        </Alert>
      )}
      {transfer.voided && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          Voided{transfer.voidReason ? `: ${transfer.voidReason}` : ""}. Excluded from totals.
        </Alert>
      )}
      {audit.status === "unavailable" ? (
        <MDTypography variant="caption" color="text" display="block" mt={1.5}>
          Lifecycle audit is unavailable. Transfer totals and status above remain available.
        </MDTypography>
      ) : audit.status === "ready" ? (
        <MDBox mt={2}>
          <MDTypography variant="button" fontWeight="bold">Lifecycle</MDTypography>
          {audit.events.map((event) => (
            <MDTypography key={event.transfer_event_id ?? event.source_event_id} variant="caption" color="text" display="block">
              {event.event_type} · {event.recorded_at} · {event.applied ? "applied" : `ignored: ${event.ignore_reason || "not applied"}`}
            </MDTypography>
          ))}
          {audit.corrections.length > 0 && (
            <>
              <MDTypography variant="button" fontWeight="bold" display="block" mt={1}>Corrections</MDTypography>
              {audit.corrections.map((correction) => (
                <MDTypography key={correction.correction_id} variant="caption" color="text" display="block">
                  {correction.action} · {correction.reason} · {correction.created_at}
                </MDTypography>
              ))}
            </>
          )}
        </MDBox>
      ) : null}
    </MDBox>
  );
}

function formatLocation(location) {
  if (
    location?.latitude == null || location?.longitude == null ||
    location.latitude === "" || location.longitude === ""
  ) return "--";
  const lat = Number(location?.latitude);
  const lng = Number(location?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : "--";
}
